import { differenceInDays } from 'date-fns';
import { defineStore } from 'pinia';
import { computed } from 'vue';

import { useVuexStore } from '@/plugins/app-context';
import type { Agency } from '@/types/Agency';
import type { User } from '@/types/User';

export type Role = 'agent' | 'repres';
export type Strategy = 'agents' | 'represes';

export const useAuthStore = defineStore('authStore', () => {
  // Преобразователь, чтобы мы могли переиспользовать и зависеть от vuex-стора $auth только в одном месте
  const vuex = useVuexStore();
  const user = computed<User | undefined>(() => vuex.$auth?.user);

  const agency = computed<Agency | undefined>(() => user.value?.agency);

  const strategy = computed<Strategy>(() => {
    const strategy = vuex.$auth?.$state?.strategy;

    if (!strategy) {
      throw new Error(`User strategy is not defined: ${ strategy }`);
    }

    return strategy;
  });

  const role = computed<Role | null>(() => {
    if (strategy.value === 'represes') {
      return 'repres';
    }

    if (strategy.value === 'agents') {
      return 'agent';
    }

    return null;
  });

  const isAggregator = computed<boolean>(() => {
    return user.value?.agency?.generalType === 'aggregator';
  });

  const loggedIn = computed<boolean>(() => vuex.$auth?.loggedIn || false);

  const hasUnreadNotifications = computed<boolean>(() => {
    return user.value?.has_unread_notices || false;
  });

  function getUserToken () {
    return vuex.$auth.strategy.token.get();
  }

  async function fetchUser () {
    await vuex.$auth.fetchUser();
  }

  function setUser (value: User) {
    vuex.$auth.setUser(value);
  }

  async function logout () {
    await vuex.$auth.logout();
  }

  function setHasUnreadNotifications (value: boolean) {
    if (user.value) {
      const updatedUser = { ...user.value, has_unread_notices: value };
      vuex.$auth.setUser(updatedUser);
    }
  }

  /**
   * Отображаем дату понижения статуса ПЛ
   */
  const isShowLoyaltyDayDowngradeStatus = computed(() => {
    if (isAggregator.value) {
      return;
    }

    const todayTime = new Date().setHours(0, 0, 0, 0);
    const dateTime = user.value?.loyalty?.date_downgrade_status
      ? new Date(user.value.loyalty.date_downgrade_status).setHours(0, 0, 0, 0)
      : 0;
    const daysStatusExpires = dateTime - todayTime > 0
      ? Math.abs(differenceInDays(dateTime, todayTime))
      : 0;

    return dateTime && daysStatusExpires > 0;
  });

  return {
    user,
    agency,
    fetchUser,
    role,
    isAggregator,
    strategy,
    loggedIn,
    hasUnreadNotifications,
    setUser,
    setHasUnreadNotifications,
    getUserToken,
    logout,
    isShowLoyaltyDayDowngradeStatus,
  };
});
